import React, { ReactNode } from 'react'
import { GlassPanel } from '@renderer/components/BackgroundContainer'
import { useDesignLayout } from '@renderer/hooks'

interface AiStageShellProps {
  /** The compare slider / processing image. */
  preview: ReactNode
  /** Title / description / badge — above the preview (portrait) or beside it (landscape). */
  header?: ReactNode
  /** Buttons / status — below the preview (portrait) or beside it (landscape). */
  footer?: ReactNode
  /** Extra GlassPanel classes (e.g. review's `animate-ai-pop`). */
  panelClassName?: string
  /** Absolutely-positioned decoration inside the panel (e.g. the review sparkle burst). */
  overlay?: ReactNode
}

/**
 * Orientation- AND size-aware shell for the AI-effect stages.
 *
 * The size rule is deliberately inverted from "bigger screen = bigger UI":
 * - **Small screens** (phones / small kiosks — `tiny` + `compact`) → FILL the viewport with a big
 *   preview: the fullscreen booth experience.
 * - **Large screens** (laptops / big or portrait monitors — `normal` + `spacious`) → a CENTERED,
 *   content-sized **modest** card with touch-clickable buttons, like the consent modal — never a
 *   blown-up panel stretched across the screen with whitespace.
 *
 * Layout: **landscape** puts the preview beside the text/buttons; **portrait** stacks them in
 * natural reading order — header, preview, footer.
 */
export const AiStageShell: React.FC<AiStageShellProps> = ({
  preview,
  header,
  footer,
  panelClassName = '',
  overlay,
}) => {
  const { isLandscape, isTiny, isCompact } = useDesignLayout()
  const fillMode = isTiny || isCompact // phone / small kiosk → fill big; larger → centered modest
  const rowLayout = isLandscape

  const outerPad = fillMode ? 'p-2' : 'p-4 sm:p-6'

  // Landscape (side-by-side) fills the viewport so the preview column has height.
  // PORTRAIT is always a COMPACT centered modal (hug content, capped + scroll) —
  // a full-height panel spread the title/preview/buttons apart with ~60% dead
  // canvas between them (owner review). Mirrors the consent modal.
  const panelClass = rowLayout
    ? fillMode
      ? 'h-full w-full max-h-[96dvh] max-w-6xl'
      : 'max-h-[90dvh] w-full max-w-4xl'
    : 'h-auto w-full max-h-[94dvh] max-w-[min(94vw,660px)]'

  const contentPad = fillMode ? (isTiny ? 'p-4' : 'p-5') : 'p-6 sm:p-8'
  const contentGap = fillMode ? (isTiny ? 'gap-3' : 'gap-4') : 'gap-4'

  // Portrait preview takes its natural size (never grows to create a dead band);
  // landscape column keeps its share of the row.
  const previewWrap = rowLayout
    ? 'flex min-h-0 w-[46%] shrink-0 items-center justify-center p-2 sm:p-4'
    : 'flex w-full shrink-0 items-center justify-center'

  // Portrait hugs (justify-start, no dead space); landscape column centers in its height.
  const sideJustify = rowLayout ? 'justify-center' : 'justify-start'
  const sideCol = `custom-scrollbar flex min-h-0 min-w-0 flex-col items-center overflow-y-auto ${sideJustify} ${contentGap} ${contentPad}`

  return (
    <div className={`relative flex min-h-0 flex-1 flex-col items-center justify-center ${outerPad}`}>
      <GlassPanel
        className={`relative flex flex-col overflow-hidden rounded-3xl ${panelClass} ${panelClassName}`}
      >
        {overlay}
        {rowLayout ? (
          <div className="flex min-h-0 w-full flex-1 flex-row overflow-hidden">
            <div className={previewWrap}>{preview}</div>
            <div className={`${sideCol} flex-1`}>
              {header}
              {footer}
            </div>
          </div>
        ) : (
          <div className={`${sideCol} w-full`}>
            {header}
            <div className={previewWrap}>{preview}</div>
            {footer}
          </div>
        )}
      </GlassPanel>
    </div>
  )
}

export default AiStageShell
